import React, { useEffect, useRef } from "react";
import { FaShieldAlt } from "react-icons/fa";

import type { TravelCategory } from "../../config/travelpayoutsWidgets";

type Props = {
  src: string;
  title: string;
  category: TravelCategory;
  minHeight: number;
  onReady?: () => void;
};

export default function TravelpayoutsWidget({
  src,
  title,
  category,
  minHeight,
  onReady,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const readyRef = useRef(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    host.innerHTML = "";
    readyRef.current = false;

    const resizeEmbeddedWidget = () => {
      const iframe = host.querySelector("iframe");
      if (!iframe) return;

      if (!readyRef.current) {
        readyRef.current = true;
        onReady?.();
      }

      iframe.style.width = "100%";
      iframe.style.maxWidth = "100%";
      iframe.style.border = "0";
      iframe.style.overflow = "visible";

      if (category === "flights") {
        const height = Math.max(minHeight, 520);
        iframe.style.minHeight = `${height}px`;
        iframe.setAttribute("height", String(height));
      } else {
        iframe.style.minHeight = `${minHeight}px`;
        iframe.setAttribute("height", String(minHeight));
      }
    };

    const observer = new MutationObserver(resizeEmbeddedWidget);
    observer.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    const script = document.createElement("script");
    script.src = src.startsWith("//") ? `https:${src}` : src;
    script.async = true;
    script.charset = "utf-8";
    host.appendChild(script);

    const resizeTimer = window.setInterval(resizeEmbeddedWidget, 400);

    return () => {
      observer.disconnect();
      window.clearInterval(resizeTimer);
      host.innerHTML = "";
    };
  }, [src, category, minHeight, onReady]);

  return (
    <div className="relative overflow-visible rounded-[2rem] border border-slate-200 bg-white p-4 shadow-2xl shadow-slate-900/10 md:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700">
          <FaShieldAlt /> Partenaire affilié
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold text-slate-500">
          Travelpayouts
        </span>
      </div>

      <div
        ref={hostRef}
        aria-label={title}
        className="relative z-20 w-full overflow-visible rounded-2xl bg-slate-50/60"
        style={{ minHeight }}
      />

      <p className="relative z-10 mt-4 text-xs leading-relaxed text-slate-500">
        Les prix, disponibilités et conditions finales sont confirmés
        directement auprès du partenaire.
      </p>
    </div>
  );
}
